import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, Clock, GraduationCap, Search, X, Sparkles, Filter, Check, Trophy } from "lucide-react";
import { toast } from "sonner";

const LEVEL_COLOR = {
  Débutant: "bg-emerald-100 text-emerald-300",
  Intermédiaire: "bg-amber-100 text-amber-700",
  Avancé: "bg-rose-100 text-rose-700",
};

function EmbedPlayer({ course, onClose }) {
  if (!course) return null;
  const src =
    course.provider === "tiktok"
      ? `https://www.tiktok.com/embed/v2/${course.video_id}`
      : `https://www.youtube-nocookie.com/embed/${course.video_id}?autoplay=1&rel=0&modestbranding=1`;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-center p-4" data-testid="course-player" onClick={onClose}>
      <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label="Fermer"
          data-testid="close-player"
          className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <div className={`relative ${course.provider === "tiktok" ? "aspect-[9/16] max-h-[85vh] mx-auto" : "aspect-video"} w-full rounded-2xl overflow-hidden shadow-2xl bg-black`}>
          <iframe
            src={src}
            title={course.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="mt-4 bg-[var(--surface)] rounded-xl p-4">
          <div className="flex items-center gap-2 text-xs mb-1">
            <span className={`px-2 py-0.5 rounded-full font-bold ${LEVEL_COLOR[course.level] || "bg-[var(--surface-alt)] text-zinc-300"}`}>{course.level}</span>
            <span className="text-zinc-500">· {course.category}</span>
            <span className="text-zinc-500">· {course.duration}</span>
          </div>
          <div className="font-display text-lg font-bold text-[var(--text)]">{course.title}</div>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{course.description}</p>
        </div>
      </div>
    </div>
  );
}

export default function Courses() {
  const [data, setData] = useState({ courses: [], categories: [], levels: [], badges: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [level, setLevel] = useState("");
  const [playing, setPlaying] = useState(null);

  const loadCourses = () => {
    setLoading(true);
    api
      .get("/courses")
      .then((r) => setData(r.data))
      .catch(() => setData({ courses: [], categories: [], levels: [], badges: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const openPlayer = async (course) => {
    setPlaying(course);
    // Mark as watched + claim XP (idempotent)
    try {
      const { data: res } = await api.post(`/courses/${course.id}/watched`);
      if (res?.xp_gained > 0) {
        toast.success(`+${res.xp_gained} XP · Cours démarré`);
      }
      (res?.new_badges || []).forEach((b) => {
        toast(`${b.emoji} Badge débloqué : ${b.title}`, { duration: 6000 });
      });
      if (res?.xp_gained > 0 || (res?.new_badges || []).length > 0) {
        loadCourses();
      }
    } catch (_) { /* swallow: non-fatal */ }
  };

  const filtered = useMemo(() => {
    let list = data.courses || [];
    if (cat) list = list.filter((c) => c.category === cat);
    if (level) list = list.filter((c) => c.level === level);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (c) => c.title.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [data, query, cat, level]);

  const watchedCount = (data.courses || []).filter((c) => c.completed).length;
  const total = (data.courses || []).length;

  return (
    <AppShell>
      <EmbedPlayer course={playing} onClose={() => setPlaying(null)} />

      <div className="px-5 pt-6 pb-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-orange-500 grid place-items-center shadow-md shadow-emerald-200 shrink-0">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-bold text-[var(--text)]" data-testid="courses-title">Prosperr Academy</h1>
            <p className="text-sm text-zinc-500">Des cours courts et concrets pour vos commerciaux — vidéos YouTube & TikTok pré-sélectionnées.</p>
          </div>
        </div>

        {total > 0 && (
          <div className="mt-4 bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm" data-testid="academy-progress">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-300">
                <Trophy className="w-4 h-4 text-amber-500" /> Votre parcours
              </div>
              <div className="text-xs font-semibold text-zinc-400">{watchedCount} / {total} cours vus</div>
            </div>
            <div className="h-2 bg-[var(--surface-alt)] rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-orange-500 transition-all"
                style={{ width: `${(watchedCount / total) * 100}%` }}
                data-testid="academy-progress-bar"
              />
            </div>
            {(data.badges || []).length > 0 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {data.badges.map((b) => {
                  const earned = watchedCount >= b.min_courses;
                  return (
                    <div
                      key={b.id}
                      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${earned ? "bg-gradient-to-r from-emerald-500 to-orange-600 text-white shadow-sm" : "bg-[var(--surface-alt)] text-zinc-500"}`}
                      title={`À ${b.min_courses} cours`}
                      data-testid={`academy-badge-${b.id}`}
                    >
                      <span className="text-base leading-none">{b.emoji}</span>
                      <span className="truncate">{b.title}</span>
                      {!earned && <span className="opacity-60">· {b.min_courses}</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-5">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <Input
            data-testid="course-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un cours (objections, LinkedIn, closing...)"
            className="pl-10 h-11 rounded-full bg-[var(--surface)] border-[var(--border)] text-[var(--text)]"
          />
        </div>

        <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1">
          <button
            onClick={() => { setCat(""); setLevel(""); }}
            className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${!cat && !level ? "bg-emerald-500 text-white" : "bg-[var(--surface)] text-zinc-400 border border-[var(--border)] hover:border-emerald-500"}`}
            data-testid="filter-all"
          >
            <Filter className="w-3 h-3" /> Tous
          </button>
          {(data.categories || []).map((c) => (
            <button
              key={c}
              onClick={() => setCat(cat === c ? "" : c)}
              className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${cat === c ? "bg-emerald-500 text-white" : "bg-[var(--surface)] text-zinc-400 border border-[var(--border)] hover:border-emerald-500"}`}
              data-testid={`filter-cat-${c}`}
            >
              {c}
            </button>
          ))}
          <span className="shrink-0 w-px bg-slate-200 mx-1 my-1" />
          {(data.levels || []).map((l) => (
            <button
              key={l}
              onClick={() => setLevel(level === l ? "" : l)}
              className={`shrink-0 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${level === l ? "bg-emerald-500 text-white" : "bg-[var(--surface)] text-zinc-400 border border-[var(--border)] hover:border-emerald-500"}`}
              data-testid={`filter-lvl-${l}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-5 grid sm:grid-cols-2 gap-4">
        {loading && (
          <div className="col-span-full text-center py-10 text-zinc-500 text-sm">Chargement…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center py-10 text-zinc-500 text-sm">Aucun cours ne correspond à vos filtres.</div>
        )}
        {filtered.map((c) => (
          <button
            key={c.id}
            onClick={() => setPlaying(c)}
            className="group text-left bg-[var(--surface)] rounded-xl overflow-hidden border border-[var(--border-soft)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
            data-testid={`course-card-${c.id}`}
          >
            <div className="relative aspect-video bg-slate-900 overflow-hidden">
              <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
              <div className="absolute top-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-300">
                {c.provider === "tiktok" ? "TikTok" : "YouTube"}
              </div>
              <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-full px-2 py-0.5 text-[10px] font-semibold text-white flex items-center gap-1">
                <Clock className="w-3 h-3" /> {c.duration}
              </div>
              <div className="absolute inset-0 grid place-items-center">
                <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur grid place-items-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-6 h-6 text-emerald-400 fill-current ml-0.5" />
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1.5">
                <span className={`px-2 py-0.5 rounded-full ${LEVEL_COLOR[c.level] || "bg-[var(--surface-alt)] text-zinc-300"}`}>{c.level}</span>
                <span className="text-zinc-500">{c.category}</span>
              </div>
              <div className="font-display font-bold text-[var(--text)] leading-tight">{c.title}</div>
              <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2 leading-relaxed">{c.description}</p>
            </div>
          </button>
        ))}
      </div>

      <div className="px-5 mt-8">
        <div className="bg-gradient-to-br from-emerald-500 to-orange-600 text-white rounded-3xl p-5 shadow-lg shadow-emerald-300/30 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 grid place-items-center shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-bold leading-tight">Un cours à suggérer ?</div>
            <div className="text-sm opacity-90 mt-0.5">Écrivez-nous à <a href="mailto:hello@prosperr.xyz" className="underline font-semibold">hello@prosperr.xyz</a> et nous l'ajouterons pour votre équipe.</div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
